<template>
	<div id="updateAvailable" hidden class="bg-red-500 text-center cursor-pointer" @click="refreshApplication">Er is een
		update beschikbaar. Klik hier om de applicatie te verversen
	</div>
	<div class="flex h-full" :class="{ 'touchscreen-mode': activeUser?.settings?.touchScreen == true }">
		<AppDebugInfo/>
		<AppGlobalSearch
			v-if="showGlobalSearch"
			@close="() => (showGlobalSearch = false)"
			@open-q-bot="() => {showQBotSearch = true; showGlobalSearch = false}"
		/>
		<AppQBotSearch
			v-if="showQBotSearch && assistantEnabled"
			@close="() => (showQBotSearch = false)"
		/>
		<template v-if="isLoading">
			<AppIcon
				class="text-black m-auto"
				size="5x"
				icon="spinner"
				spin-pulse
			/>
		</template>

		<div class="flex flex-row gap-1 w-full overflow-hidden" v-else-if="authenticated && activeUser && activeCompany">
			<AppActionCenter/>
			<Sidebar/>
			<div class="flex flex-col w-full my-1 mr-1">
				<WindowTabBar/>
				<div
					id="app-tab-content"
					class="h-full w-full flex grow border-2 border-primary rounded rounded-tl-none overflow-y-auto"
				>
					<template v-for="tab in sessionTabs" :key="tab.fullPath">
						<div class="h-full w-full flex grow" v-show="tab.fullPath === activeTab?.fullPath">
							<Component
								v-if="resolveTabComponent(tab)"
								:is="resolveTabComponent(tab)"
								:key="tab.fullPath"
								v-bind="{ ...(tab.props ?? {}), tab, activeTab }"
							/>
						</div>
					</template>
					<Dashboard :key="'dashboard'" v-show="activeTab == null"/>
				</div>
			</div>
		</div>
		<template v-else>
			<SecurityLogin/>
		</template>
		<Toaster
			rich-colors
			close-button
		/>
	</div>
</template>

<script setup lang="ts">
import { Toaster } from "vue-sonner";
import { currentSession } from "@q-food/q-food";
import SecurityLogin from "~/views/security.login.vue";
import { useAppStore } from "~stores/App";
import { useFeaturesStore } from "~stores/Features";
import { useUserStore } from "~stores/User";
import { resolveTabComponent, useAppTabs } from "./stores/useAppTabs";
import Dashboard from "~/views/dashboard.vue";
import { useApiResources } from "~stores/ApiResources";
import { setPendingGridFilter } from "~utils/Grids/AssistantGridFilter";

const { appTitle, showGlobalSearch, showQBotSearch } = storeToRefs(useAppStore());
const { authenticated, activeUser, activeCompany } = storeToRefs(useUserStore());

// The assistant is opt-in server-side and its endpoints 404 while off, so its entrypoints stay hidden
// until the server confirms it is enabled. Loaded once the user is authenticated (the call is
// authenticated like every other), and defaults to off until then.
const featuresStore = useFeaturesStore();
const { assistant: assistantEnabled } = storeToRefs(featuresStore);
watch(
	authenticated,
	(isAuthenticated) => {
		if (isAuthenticated) featuresStore.load();
	},
	{ immediate: true },
);

const { activeTab, closeCurrentTab, sessionTabs } = useAppTabs();

// Resolved during setup so they're usable from the websocket-triggered handler below (the custom
// useRouter resolves vue-router via injection, which only works inside setup — not in a later callback).
const { push: pushTab } = useRouter();
const apiResources = useApiResources();

const { left, right } = useMagicKeys();

function focusAdjacentButton(direction: number) {
	const buttons = Array.from(document.querySelectorAll("button:not([disabled])")) as HTMLButtonElement[];
	const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
	if (currentIndex === -1) return;

	const nextIndex = (currentIndex + direction + buttons.length) % buttons.length;
	buttons[nextIndex].focus();
}

whenever(right, () => {
	if (document.activeElement instanceof HTMLButtonElement) {
		focusAdjacentButton(1);
	}
});

whenever(left, () => {
	if (document.activeElement instanceof HTMLButtonElement) {
		focusAdjacentButton(-1);
	}
});

/**
 * Register in-app keyboard shortcuts
 */
usePredefinedKeyStroke("window.closeTab", closeCurrentTab);
usePredefinedKeyStroke("window.toggleGlobalSearch", () => {
	showGlobalSearch.value = true;
});
usePredefinedKeyStroke("window.toggleQBot", () => {
	if (!assistantEnabled.value) return;
	showQBotSearch.value = true;
});

/**
 * Assistant "open a grid" action: the websocket handler dispatches `food:grid:open` with { resource,
 * criteria }. Stash the RSQL criteria (consumed by the resource's AppDataGrid at grid-ready) and open the
 * resource's grid in a new tab; vue-router resolves the custom/split-grid view automatically.
 */
function openGridFromAssistant(event: Event) {
	// Only the assistant emits this; ignore it outright when the server has the assistant switched off.
	if (!assistantEnabled.value) return;
	const { resource, criteria } = (event as CustomEvent).detail as { resource: string; criteria: string };
	const found = apiResources.findByNameSafe(resource);
	if (!found) {
		useToast().warning(`Onbekend overzicht: ${resource}`);
		return;
	}
	if (criteria) setPendingGridFilter(found.name, criteria);
	pushTab(`/${found.name}/index`);
}

onMounted(() => window.addEventListener("food:grid:open", openGridFromAssistant));
onUnmounted(() => window.removeEventListener("food:grid:open", openGridFromAssistant));

// Load the current session and then refresh the user info and sidebar
const isLoading = ref(true);
currentSession()
	.then(async () => {
		await useAppStore().refreshApp();
	})
	.catch((response) => {
		switch (response.status) {
			case 403:
				// no need to show message
				break;
			default:
				console.log(response);
				const errorMessage = response.message ?? "Er is iets misgegaan met het ophalen van de gebruikersgegevens";
				throw Error(errorMessage);
		}
	})
	.finally(() => {
		isLoading.value = false;
	});

function refreshApplication() {
	window.location.reload();
}

useTitle(appTitle);
</script>

<style scoped></style>
